import nacl from 'tweetnacl';
import { deriveBoxKeyPair, arrayBufferToBase64, base64ToArrayBuffer } from './identity';
import type { UserIdentity } from './identity';

export interface Message {
  id: string;
  fromUserId: string;
  fromUsername: string;
  toUserId: string;
  text: string;
  createdAt: number;
  read: boolean;
}

const MESSAGES_KEY = 'authenticulture_messages';
const BOX_KEYS_KEY = 'authenticulture_box_pubkeys';

export function storeBoxPubkey(userId: string, boxPubkey: string): void {
  const stored = JSON.parse(localStorage.getItem(BOX_KEYS_KEY) || '{}');
  stored[userId] = boxPubkey;
  localStorage.setItem(BOX_KEYS_KEY, JSON.stringify(stored));
}

export function getBoxPubkey(userId: string): string | null {
  const stored = JSON.parse(localStorage.getItem(BOX_KEYS_KEY) || '{}');
  return stored[userId] ?? null;
}

type MessageListener = () => void;
let listeners: MessageListener[] = [];

export function addMessageListener(fn: MessageListener): () => void {
  listeners.push(fn);
  return () => { listeners = listeners.filter(l => l !== fn); };
}

function notify() {
  listeners.forEach(fn => fn());
}

export function encryptDM(text: string, recipientBoxPubkey: string, senderIdentity: UserIdentity): { nonce: string; ciphertext: string; fromBoxPubkey: string } {
  const senderBox = deriveBoxKeyPair(senderIdentity);
  const recipientPubkey = base64ToArrayBuffer(recipientBoxPubkey);
  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const msg = new TextEncoder().encode(text);
  const ciphertext = nacl.box(msg, nonce, recipientPubkey, senderBox.secretKey);
  return {
    nonce: arrayBufferToBase64(nonce),
    ciphertext: arrayBufferToBase64(ciphertext),
    fromBoxPubkey: arrayBufferToBase64(senderBox.publicKey),
  };
}

export function decryptDM(ciphertext: string, nonce: string, fromBoxPubkey: string, myIdentity: UserIdentity): string | null {
  try {
    const myBox = deriveBoxKeyPair(myIdentity);
    const senderPubkey = base64ToArrayBuffer(fromBoxPubkey);
    const ct = base64ToArrayBuffer(ciphertext);
    const nc = base64ToArrayBuffer(nonce);
    const decrypted = nacl.box.open(ct, nc, senderPubkey, myBox.secretKey);
    if (!decrypted) return null;
    return new TextDecoder().decode(decrypted);
  } catch {
    return null;
  }
}

function getAllMessages(): Message[] {
  const stored = localStorage.getItem(MESSAGES_KEY);
  return stored ? JSON.parse(stored) : [];
}

function saveMessages(messages: Message[]): void {
  localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
}

export function saveIncomingDM(params: {
  id: string;
  fromUserId: string;
  fromUsername: string;
  toUserId: string;
  ciphertext: string;
  nonce: string;
  fromBoxPubkey: string;
  createdAt: number;
  myIdentity: UserIdentity;
}): void {
  const messages = getAllMessages();
  if (messages.find(m => m.id === params.id)) return;
  // Validate box pubkey is a well-formed 32-byte Curve25519 key before trusting it (M-4)
  try { if (base64ToArrayBuffer(params.fromBoxPubkey).length !== 32) return; } catch { return; }
  const text = decryptDM(params.ciphertext, params.nonce, params.fromBoxPubkey, params.myIdentity);
  if (!text) return;
  storeBoxPubkey(params.fromUserId, params.fromBoxPubkey);
  messages.push({
    id: params.id,
    fromUserId: params.fromUserId,
    fromUsername: params.fromUsername,
    toUserId: params.toUserId,
    text,
    createdAt: params.createdAt,
    read: false,
  });
  saveMessages(messages);
  notify();
}

export function saveOwnSentDM(params: {
  id: string;
  fromUserId: string;
  fromUsername: string;
  toUserId: string;
  text: string;
  createdAt: number;
}): void {
  const messages = getAllMessages();
  if (messages.find(m => m.id === params.id)) return;
  messages.push({ ...params, read: true });
  saveMessages(messages);
  notify();
}

export function deleteConversation(myUserId: string, otherUserId: string): void {
  const stored = localStorage.getItem(MESSAGES_KEY);
  const messages: Message[] = stored ? JSON.parse(stored) : [];
  const remaining = messages.filter(m =>
    !((m.fromUserId === myUserId && m.toUserId === otherUserId) ||
      (m.fromUserId === otherUserId && m.toUserId === myUserId))
  );
  localStorage.setItem(MESSAGES_KEY, JSON.stringify(remaining));
  listeners.forEach(fn => fn());
}

export function getConversation(userId1: string, userId2: string): Message[] {
  return getAllMessages()
    .filter(m =>
      (m.fromUserId === userId1 && m.toUserId === userId2) ||
      (m.fromUserId === userId2 && m.toUserId === userId1)
    )
    .sort((a, b) => a.createdAt - b.createdAt);
}

export function getConversations(myUserId: string): Array<{ otherId: string; otherUsername: string; lastAt: number; unread: number }> {
  const messages = getAllMessages();
  const map = new Map<string, { username: string; lastAt: number; unread: number }>();
  for (const m of messages) {
    const isFrom = m.fromUserId === myUserId;
    const otherId = isFrom ? m.toUserId : m.fromUserId;
    const otherUsername = isFrom ? '' : m.fromUsername;
    const existing = map.get(otherId);
    const unreadDelta = (!isFrom && !m.read) ? 1 : 0;
    if (!existing) {
      map.set(otherId, { username: otherUsername || otherId, lastAt: m.createdAt, unread: unreadDelta });
    } else {
      if (m.createdAt > existing.lastAt) existing.lastAt = m.createdAt;
      if (otherUsername) existing.username = otherUsername;
      existing.unread += unreadDelta;
    }
  }
  return Array.from(map.entries())
    .map(([otherId, d]) => ({ otherId, otherUsername: d.username, lastAt: d.lastAt, unread: d.unread }))
    .sort((a, b) => b.lastAt - a.lastAt);
}

export function markAsRead(myUserId: string, otherUserId: string): void {
  const messages = getAllMessages();
  let changed = false;
  for (const m of messages) {
    if (m.fromUserId === otherUserId && m.toUserId === myUserId && !m.read) {
      m.read = true;
      changed = true;
    }
  }
  if (changed) {
    saveMessages(messages);
    notify();
  }
}
